import { useState } from 'react';
import { Stack, Card, Text, Button, Badge, Group } from '@repletoryreach/sdk-ui';
import { PageHeader, Alert, EmptyState, DataTable } from '@repletoryreach/sdk-ui/data-display';
import { useAffiliate, useAffiliateReferrals } from '../../../data/useAffiliate';
import { formatMinor, formatDate } from '../../../lib/format';
import type { Indexed } from '../../../lib/types';
import { useT } from '../../../i18n/I18nProvider';
import '../refer.css';

/**
 * The companies this partner introduced.
 *
 * What is deliberately not here: what any of them spends. A partner is told
 * the name, the day it started, whether it is still earning, and their own
 * share. The company's bill is the company's business.
 */
export default function ReferReferralsPage() {
  const t = useT();
  const me = useAffiliate();
  const [status, setStatus] = useState<string | undefined>(undefined);
  const referrals = useAffiliateReferrals(status);

  if (!me.isLoading && !me.data) {
    return (
      <Stack gap="lg">
        <PageHeader eyebrow={t('refer.eyebrow')} title={t('refer.referrals.title')} />
        <EmptyState title={t('refer.notEnrolled.title')} description={t('refer.notEnrolled.body')} />
      </Stack>
    );
  }

  const rows = (referrals.data?.data ?? []).map((r, i) => ({ ...r, _i: i }));
  const filters: { value?: string; labelKey: string }[] = [
    { value: undefined, labelKey: 'refer.filter.all' },
    { value: 'earning', labelKey: 'refer.filter.earning' },
    { value: 'ended', labelKey: 'refer.filter.ended' },
    { value: 'revoked', labelKey: 'refer.filter.revoked' }
  ];

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('refer.eyebrow')}
        title={t('refer.referrals.title')}
        description={t('refer.referrals.description')}
      />

      <Group gap="sm">
        {filters.map((f) => (
          <Button
            key={f.labelKey}
            size="sm"
            variant={status === f.value ? 'filled' : 'light'}
            onClick={() => setStatus(f.value)}
          >
            {t(f.labelKey)}
          </Button>
        ))}
      </Group>

      <Card withBorder>
        {referrals.isLoading ? (
          <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('common.loading')}</Text>
        ) : referrals.isError ? (
          <Alert color="error" role="alert">
            {t('refer.referrals.loadFailed')}{' '}
            <Button variant="subtle" size="sm" onClick={() => void referrals.refetch()}>{t('action.retry')}</Button>
          </Alert>
        ) : rows.length === 0 ? (
          <EmptyState
            title={status ? t('refer.referrals.noneMatch') : t('refer.referrals.emptyTitle')}
            description={status ? t('refer.referrals.noneMatchBody') : t('refer.referrals.emptyBody')}
          />
        ) : (
          <DataTable<Indexed<(typeof rows)[number]>>
            idAccessor="_i"
            records={rows}
            columns={[
              { accessor: 'referred_name', title: t('refer.column.company'), render: (r) => r.referred_name ?? t('refer.company.unnamed') },
              { accessor: 'attributed_at', title: t('refer.column.since'), render: (r) => formatDate(r.attributed_at) },
              { accessor: 'status', title: t('refer.column.status'), render: (r) => <Badge>{t(`refer.status.${r.status}`, r.status)}</Badge> },
              { accessor: 'first_usage_at', title: t('refer.column.firstUsage'), render: (r) => r.first_usage_at ? formatDate(r.first_usage_at) : t('refer.column.notYet') },
              { accessor: 'earned_minor', title: t('refer.column.earned'), render: (r) => <span className="rr-refer-amount">{formatMinor(r.earned_minor)}</span> }
            ]}
          />
        )}
      </Card>
    </Stack>
  );
}
